import { useToggle } from 'usehooks-ts'
import { useCallback, useState } from 'react'
import { styled } from '@mui/material/styles'
import { asyncDelay } from '@weblite/logic-toolkit'
import { IconButton, Typography } from '@mui/material'
import { ClearIcon, SendIcon } from '@weblite/icons'
import { useHistoryCustomBack } from 'weblite-router'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { t } from '$i18n'
import { nothing } from '$helper/functions/utils'
import { BottomDrawer } from '$common/bottom-drawer'
import { TextEditor } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { RecordedVoice } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/types'
import { VoicePreview } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/voice-preview'
import { showEmojiPickerPopover } from '../emoji-picker'
import { ConfirmationDialog } from '../confirmation-dialog'

const editorId = 'send-voice-dialog'

interface Props {
  recordedVoice: RecordedVoice
  onConfirm: (caption?: string) => unknown
  onCancel: () => unknown
}

export function SendVoiceDialog({ recordedVoice, onConfirm, onCancel }: Props) {
  const [caption, setCaption] = useState('')

  const [isConfirmingClose, toggleIsConfirmingClose] = useToggle(false)

  const handleClose = useCallback(async () => {
    closeCurrentModal()
    await asyncDelay(300)
    onCancel()
  }, [onCancel])

  useHistoryCustomBack(toggleIsConfirmingClose, [toggleIsConfirmingClose])

  const handleConfirm = useCallback(async () => {
    await closeCurrentModal()
    onConfirm(caption)
  }, [caption, onConfirm])

  return (
    <>
      <BottomDrawer isOpen onClose={toggleIsConfirmingClose}>
        <Column>
          <Header>
            <IconButton onClick={toggleIsConfirmingClose}>
              <ClearIcon sx={{ color: 'neutral700' }} />
            </IconButton>
            <Typography>{t('chat.sendVoiceDialog.title')}</Typography>
          </Header>
          <PreviewWrapper>
            <VoicePreview
              recorderVoice={recordedVoice}
              onReset={handleClose}
              onSend={nothing}
              showSendButton={false}
            />
          </PreviewWrapper>
          <SendFieldWrapper>
            <IconButton onClick={handleConfirm}>
              <SendIcon sx={{ color: 'primary500' }} />
            </IconButton>

            <TextEditor
              action={undefined}
              mentionIsDisabled={false}
              darkMode={false}
              placeholder={t('chat.sendField')}
              setHasMessage={nothing}
              onContentChange={setCaption}
              setMentionQuery={nothing}
              onSubmit={handleConfirm}
              onPasteFile={nothing}
              sx={{
                minHeight: '40px!important',
                boxSizing: 'border-box',
                marginLeft: 0,
              }}
              editorId={editorId}
              onClickEmojiIcon={e => {
                e.stopPropagation()
                showEmojiPickerPopover({
                  anchorEl: e.currentTarget,
                  editorId,
                })
              }}
            />
          </SendFieldWrapper>
        </Column>
      </BottomDrawer>
      <ConfirmationDialog
        isOpen={isConfirmingClose}
        title={t(`confirmDialog.discardVoiceMessage.title`)}
        description={t(`confirmDialog.discardVoiceMessage.description`)}
        confirmOption={t('common.remove')}
        cancelOption={t('common.cancel')}
        alertMode
        onCancel={toggleIsConfirmingClose}
        onConfirm={handleClose}
      />
    </>
  )
}

const Header = styled('div')`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  align-items: center;
  height: 64px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral300};
  padding: 12px 8px;
`

const Column = styled('div')`
  display: flex;
  flex-direction: column;
  width: 400px;
`

const PreviewWrapper = styled('div')`
  width: 100%;
`

const SendFieldWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral300};
  padding: 12px;
  width: 100%;
`

const TextFieldWrapper = styled('div')`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.neutral200};
  background-color: ${({ theme }) => theme.palette.neutral000};
  border-radius: 12px;
  padding: 4px;
  display: flex;
  align-items: center;
  gap: 2px;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary50};
  }

  &:focus-within {
    background-color: ${({ theme }) => theme.palette.neutral000};
    outline: 1px solid ${({ theme }) => theme.palette.primary300};
    border-color: ${({ theme }) => theme.palette.primary300};
    caret-color: ${({ theme }) => theme.palette.primary300};
  }
`
